import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { setTableCurrentPage } from '../state/actions/ui/tablePages';
import { getTableCurrentPage } from '../state/selectors/ui/tables';
import { store } from '../state/configureStore';

type SetCurrentPage = (next: number | ((prev: number) => number)) => void;

/**
 * Persiste `currentPage` del paginador en Redux `tablePages[tableId]`.
 *
 * **Bug que arregla** (C-11.7, 2026-05-06): antes el `currentServerPage` vivía
 * como `useState(1)` LOCAL del componente. Al navegar a un detail (ej.
 * `/work-orders/accidents/{id}` desde la lista) y volver, el componente
 * `AccidentsTable` se re-montaba y el state local volvía a 1, mientras el
 * cursor (sí persistido en Redux) seguía apuntando a la página 5 →
 * inconsistencia: la tabla mostraba items de página 5 pero el paginador
 * decía "1 de 6". Reproducible en las 10 tablas que usan el patrón.
 *
 * **Por qué un slice separado (`tablePages`) en lugar de meter `currentPage`
 * dentro de `cursorTables[id]`**: los 30+ callsites que dispatchean
 * `setCursor(id, dataCursor)` hacen MERGE del objeto (el reducer mezcla los
 * campos nuevos con el estado previo). Sin embargo, si `currentPage` viviera
 * ahí, cualquier filtro o paginación de cursor podría pisar el valor, y haría
 * falta coordinar el orden de dispatch en cada callsite. Con un slice
 * independiente las 2 acciones son ortogonales: `cursorTables` y `tablePages`
 * no se pisan entre sí, y los 10 tables no necesitan cambios.
 *
 * **API drop-in con `useState`**: misma firma — soporta valor directo y
 * callback con `prev`. Para evitar stale closures cuando el callback se
 * encadena (ej. `setCurrentServerPage((prev) => prev + 1)` dentro de un
 * `await`), leemos el state más reciente directamente del store, no del
 * snapshot del render.
 *
 * **Modales NO usan este hook** — su comportamiento contextual (resetear a 1
 * al cerrar/reabrir) requiere `useState(1)` local.
 */
export function useTableCurrentPage(
  tableId: string,
): [number, SetCurrentPage] {
  const dispatch = useDispatch();
  const currentPage = useSelector(getTableCurrentPage(tableId)) ?? 1;

  const setCurrentPage = useCallback<SetCurrentPage>(
    (next) => {
      // Leer state fresco desde el store evita stale closures cuando este
      // setter se invoca dentro de callbacks async. Imprescindible para el
      // patrón `setCurrentServerPage((prev) => prev + 1)`.
      const fresh = getTableCurrentPage(tableId)(store.getState()) ?? 1;
      const value = typeof next === 'function' ? next(fresh) : next;
      dispatch(setTableCurrentPage(tableId, value));
    },
    [dispatch, tableId],
  );

  return [currentPage, setCurrentPage];
}
